import React from 'react';
import { ArrowLeft, Crown, Star, ShieldCheck } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const Template16 = () => {
    const navigate = useNavigate();

    return (
        <div className="w-full min-h-screen bg-gray-50 font-serif">
            {/* Luxury Header */}
            <header className="bg-white shadow-sm border-b border-gray-200 sticky top-0 z-50">
                <div className="w-full px-4 lg:px-12 py-4 lg:py-6 flex justify-between items-center">
                    <button
                        onClick={() => navigate('/TemplateThemes')}
                        className="flex items-center gap-2 px-4 py-2 text-gray-600 hover:text-black transition-colors"
                    >
                        <ArrowLeft size={18} />
                        <span className="text-sm font-medium">Return</span>
                    </button>
                    <div className="text-center">
                        <h1 className="text-2xl lg:text-4xl font-bold text-black tracking-wider">PRESTIGE</h1>
                        <p className="text-xs lg:text-sm text-gray-500 tracking-widest">EST. 1987</p>
                    </div>
                    <div className="flex items-center gap-3">
                        <Crown className="text-yellow-600" size={24} />
                    </div>
                </div>
            </header>

            {/* Elegant Hero */}
            <section className="relative h-[80vh] lg:h-screen">
                <img
                    src="https://images.unsplash.com/photo-1512436991641-6745cdb1723f?w=1920&h=1080&fit=crop"
                    alt="Luxury Fashion"
                    className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-black/30"></div>
                <div className="absolute inset-0 flex items-center justify-center">
                    <div className="text-center text-white max-w-4xl px-4">
                        <h2 className="text-5xl lg:text-8xl font-light mb-6 tracking-wider">Timeless</h2>
                        <h3 className="text-3xl lg:text-5xl font-bold mb-8 tracking-wide">ELEGANCE</h3>
                        <p className="text-lg lg:text-xl font-light mb-10 max-w-2xl mx-auto leading-relaxed">
                            Discover our exclusive collection of luxury fashion crafted for the discerning individual
                        </p>
                        <button className="bg-white text-black px-10 lg:px-16 py-4 lg:py-5 text-sm lg:text-base tracking-widest hover:bg-gray-100 transition-all duration-500 font-medium">
                            EXPLORE COLLECTION
                        </button>
                    </div>
                </div>
            </section>

            {/* Luxury Statement */}
            <section className="bg-white py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-12 text-center">
                    <Crown className="mx-auto mb-8 text-yellow-600" size={48} />
                    <h2 className="text-3xl lg:text-5xl font-light text-black mb-6 tracking-wide">Heritage of Excellence</h2>
                    <p className="text-lg text-gray-600 max-w-3xl mx-auto leading-relaxed">
                        For over three decades, we have been crafting exceptional pieces that embody sophistication,
                        quality, and timeless style. Each piece tells a story of uncompromising craftsmanship.
                    </p>
                </div>
            </section>

            {/* Luxury Product Grid */}
            <section className="bg-gray-50 py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-light text-black mb-4 tracking-wide">Signature Collection</h2>
                        <div className="w-24 h-px bg-black mx-auto"></div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 lg:gap-12">
                        {[1, 2, 3, 4, 5, 6].map(id => (
                            <div key={id} className="group bg-white overflow-hidden shadow-sm hover:shadow-xl transition-all duration-700">
                                <div className="aspect-[3/4] overflow-hidden relative">
                                    <img
                                        src={`https://images.unsplash.com/photo-1594633312681-425c7b97ccd1?w=400&h=500&fit=crop&q=90&auto=format&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&crop=entropy&cs=tinysrgb&fm=jpg&q=90&fit=max&ixlib=rb-4.0.3&ixid=eyJhcHBfaWQiOjF9&auto=format&s=${id * 17 + 200}`}
                                        alt={`Luxury Item ${id}`}
                                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-1000"
                                    />
                                    <div className="absolute top-4 left-4">
                                        <ShieldCheck className="text-white bg-black/50 p-1 rounded" size={28} />
                                    </div>
                                </div>
                                <div className="p-6 lg:p-8">
                                    <div className="flex justify-between items-start mb-3">
                                        <h3 className="text-xl font-medium text-black tracking-wide">Signature Piece {id}</h3>
                                        <div className="flex">
                                            {[...Array(5)].map((_, i) => (
                                                <Star key={i} size={14} className="fill-yellow-400 text-yellow-400" />
                                            ))}
                                        </div>
                                    </div>
                                    <p className="text-sm text-gray-500 mb-4 tracking-wide">Premium Collection</p>
                                    <div className="flex justify-between items-center">
                                        <span className="text-2xl font-light text-black">${(299 + id * 50)}.00</span>
                                        <button className="text-sm tracking-widest text-black border border-black px-6 py-2 hover:bg-black hover:text-white transition-all duration-300">
                                            SELECT
                                        </button>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Luxury Services */}
            <section className="bg-black py-16 lg:py-24">
                <div className="container mx-auto px-4 lg:px-12">
                    <div className="text-center mb-16">
                        <h2 className="text-4xl lg:text-6xl font-light text-white mb-6 tracking-wide">Prestige Services</h2>
                        <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                            Experience our exclusive services designed for our valued clientele
                        </p>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                        {[
                            { title: "Personal Styling", desc: "One-on-one consultation" },
                            { title: "Custom Tailoring", desc: "Bespoke alterations" },
                            { title: "VIP Access", desc: "Exclusive collections" }
                        ].map((service, index) => (
                            <div key={index} className="text-center p-8 border border-gray-800 hover:border-white transition-colors duration-500">
                                <Crown className="mx-auto mb-4 text-yellow-600" size={36} />
                                <h3 className="text-xl font-medium text-white mb-2 tracking-wide">{service.title}</h3>
                                <p className="text-gray-400 text-sm tracking-wide">{service.desc}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="bg-white py-12 lg:py-16 border-t border-gray-200">
                <div className="container mx-auto px-4 lg:px-12 text-center">
                    <h3 className="text-3xl font-light mb-4 tracking-wider">PRESTIGE</h3>
                    <p className="text-gray-500 mb-6 tracking-wide">Crafting Excellence Since 1987</p>
                    <div className="flex justify-center gap-8 text-sm text-gray-400 tracking-widest">
                        <span>PRIVACY</span>
                        <span>TERMS</span>
                        <span>CONTACT</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-8 tracking-wider">&copy; 2025 Prestige. All rights reserved.</p>
                </div>
            </footer>
        </div>
    );
};

export default Template16;